import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import membershipApi from '../services/membershipApi';
import { useAuth } from './AuthContext';

// Define service costs
export const SERVICE_COSTS = {
  'job_tailor': 10,
  'job_matching': 5,
  'resume_analysis': 5,
  'resume_polish': 10,
  'resume_upload': 2,
  'interview_question': 5,
  'cover_letter': 5
};

const BalanceContext = createContext();

export const useBalance = () => useContext(BalanceContext);

export const BalanceProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [creditBalance, setCreditBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  
  // Refresh interval in milliseconds (5 minutes)
  const REFRESH_INTERVAL = 5 * 60 * 1000;

  // Fetch credit balance
  const fetchCreditBalance = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return null;
    }

    try {
      const response = await membershipApi.getCreditBalance();
      setCreditBalance(response.data);
      setLastRefreshed(Date.now());
      return response.data;
    } catch (error) {
      console.error('Error fetching credit balance:', error);
      return null;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Initial fetch on authentication change
  useEffect(() => {
    fetchCreditBalance();
  }, [isAuthenticated, fetchCreditBalance]);

  // Periodic refresh
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const intervalId = setInterval(() => {
      fetchCreditBalance();
    }, REFRESH_INTERVAL);
    
    return () => clearInterval(intervalId);
  }, [isAuthenticated, fetchCreditBalance]);

  // Check if user has enough credits for a service
  const hasEnoughCredits = useCallback((serviceType) => {
    if (!creditBalance) return false;
    
    const cost = SERVICE_COSTS[serviceType] || 0;
    return creditBalance.credits >= cost;
  }, [creditBalance]);

  // Get cost for a service
  const getServiceCost = useCallback((serviceType) => {
    return SERVICE_COSTS[serviceType] || 0;
  }, []);

  // Manually refresh balance (to be called after using a service)
  const refreshBalance = useCallback(async () => {
    return await fetchCreditBalance();
  }, [fetchCreditBalance]);

  // Deduct credits locally (optimistic update)
  const deductCredits = useCallback((amount) => {
    if (creditBalance) {
      setCreditBalance(prev => ({
        ...prev,
        credits: Math.max(0, prev.credits - amount)
      }));
    }
  }, [creditBalance]);

  const value = {
    creditBalance,
    loading,
    lastRefreshed,
    hasEnoughCredits,
    getServiceCost,
    refreshBalance,
    deductCredits,
    SERVICE_COSTS
  };

  return (
    <BalanceContext.Provider value={value}>
      {children}
    </BalanceContext.Provider>
  );
};